import { useEffect, useId, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Button, Card, Fieldset, Group, Text } from '@mantine/core';
import { sameDraft } from '../edit/same';
import { ProblemList } from './ErrorNote';
import { useUnsavedChanges } from './UnsavedChanges';

/**
 * Read first, edit on purpose: the one implementation every settings section uses.
 *
 * A settings screen that opens as a live form cannot tell anybody what is currently set, because
 * every field looks the same whether or not it was meant to be changed, and a stray keystroke on a
 * screen somebody opened to check something is a change to what readers see. So a section shows
 * what it holds, in words, and a form appears only when somebody presses Edit.
 *
 * It is one component rather than a toggle grown per screen on purpose. Six versions of this idea
 * would drift within a month, and the parts that are easy to get subtly wrong are exactly the parts
 * that would drift: what counts as changed, whether cancelling really reverts, and whether somebody
 * who cannot save is offered the button at all.
 *
 * Three things it settles for every screen that uses it:
 *
 * - The draft is this section's own, seeded from what is saved at the moment Edit is pressed and
 *   thrown away on cancel. Nothing typed here reaches the page's copy of the record, which is why
 *   cancel genuinely reverts and why a screen with several sections cannot save one section's
 *   half finished work while sending another's.
 * - Somebody who cannot write is not offered Edit at all, rather than a form that refuses them.
 * - Changes are registered with the guard while they exist, so leaving asks first, and while a form
 *   is open but untouched it asks nothing.
 */
export interface ReadEditProps<T> {
  /** What is being edited, in the words the buttons use: "the theme" gives "Edit the theme". */
  noun: string;
  /** False for anybody the API would refuse. They get the read view and no way into a form. */
  canEdit: boolean;
  /** What editing starts from, read from what is saved now. Called once, when Edit is pressed. */
  open: () => T;
  /** The form. Drawn only while somebody is editing. */
  form: (draft: T, set: (next: T) => void) => ReactNode;
  /** What has to be fixed before anything is sent, in sentences. */
  check?: (draft: T) => readonly string[];
  /**
   * Sends it. True closes the form; false leaves it open, for a screen that has put the server's
   * refusal against the field that caused it.
   */
  save: (draft: T) => Promise<boolean>;
  /**
   * Follows the draft for anything drawn outside this section: a preview of what is being typed,
   * or a button that must not stay live while the form is open. Null while nobody is editing.
   */
  onDraft?: (draft: T | null) => void;
  /** The values as they are, laid out to be read. */
  children?: ReactNode;
  /** What the way in says. "Edit" for a section that holds something, a sentence for a new thing. */
  openLabel?: string;
  /** Its accessible name, when the visible word is not enough to tell two of them apart. */
  openName?: string;
  saveLabel?: string;
  saveName?: string;
  /** What the button says while the request is in flight. */
  busyLabel?: string;
  problemsTitle?: string;
  /**
   * Whether the form is a form element, so Enter sends it.
   *
   * On for anything somebody types into and then submits, which is every panel that makes a new
   * thing. Off where the section is a set of controls rather than a question being answered.
   */
  asForm?: boolean;
}

export function ReadEdit<T>({
  noun,
  canEdit,
  open,
  form,
  check,
  save,
  onDraft,
  children,
  openLabel = 'Edit',
  openName,
  saveLabel = 'Save',
  saveName,
  busyLabel = 'Saving...',
  problemsTitle = 'Before this can be saved:',
  asForm = false,
}: ReadEditProps<T>) {
  const id = useId();
  const { hold, release } = useUnsavedChanges();

  // One piece of state rather than two: a section is either reading or holding a draft and what
  // that draft started as, and there is no third state where one of the two exists on its own.
  const [session, setSession] = useState<{ draft: T; start: T } | null>(null);
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [saving, setSaving] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  const unsaved = session !== null && !sameDraft(session.draft, session.start);

  useEffect(() => {
    hold(id, unsaved);
    return () => release(id);
  }, [hold, release, id, unsaved]);

  function begin() {
    const seed = open();
    setSession({ draft: seed, start: seed });
    setProblems([]);
    setDiscarding(false);
    onDraft?.(seed);
  }

  function change(next: T) {
    setSession((current) => (current === null ? null : { ...current, draft: next }));
    onDraft?.(next);
  }

  function close() {
    setSession(null);
    setProblems([]);
    setDiscarding(false);
    onDraft?.(null);
  }

  async function submit() {
    if (session === null) return;
    const found = check?.(session.draft) ?? [];
    if (found.length > 0) {
      setProblems(found);
      return;
    }
    setProblems([]);
    setSaving(true);
    try {
      if (await save(session.draft)) close();
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    if (unsaved) setDiscarding(true);
    else close();
  }

  if (session === null) {
    return (
      <>
        {children}
        {canEdit && (
          <Button
            variant="default"
            size="sm"
            className="read-edit-trigger"
            mt="sm"
            onClick={begin}
            aria-label={openName ?? `${openLabel} ${noun}`}
          >
            {openLabel}
          </Button>
        )}
      </>
    );
  }

  const body = (
    <>
      <ProblemList problems={problems} title={problemsTitle} />
      <Fieldset variant="unstyled" disabled={saving} p={0} m={0}>
        {form(session.draft, change)}
      </Fieldset>
      <Group mt="md">
        <Button
          type={asForm ? 'submit' : 'button'}
          onClick={asForm ? undefined : () => void submit()}
          disabled={saving}
          aria-label={saveName ?? `${saveLabel} ${noun}`}
        >
          {saving ? busyLabel : saveLabel}
        </Button>
        <Button
          type="button"
          variant="default"
          disabled={saving}
          onClick={cancel}
          aria-label={`Cancel editing ${noun}`}
        >
          Cancel
        </Button>
      </Group>

      {discarding && (
        <Card withBorder radius="md" p="md" mt="md">
          <Text size="sm" fw={500}>
            Throw away the changes to {noun}?
          </Text>
          <Text size="sm" mt={4}>
            Nothing has been saved, so what is on screen goes back to what it was before you started
            and there is no way to get it back.
          </Text>
          <Group mt="sm">
            <Button color="red" size="compact-sm" onClick={close} autoFocus>
              Throw them away
            </Button>
            <Button variant="default" size="compact-sm" onClick={() => setDiscarding(false)}>
              Carry on editing
            </Button>
          </Group>
        </Card>
      )}
    </>
  );

  if (!asForm) return body;

  return (
    <form
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        void submit();
      }}
    >
      {body}
    </form>
  );
}
